import React, { MouseEvent, useRef } from 'react';
import cn from 'clsx';
import { useStoreon } from 'storeon/react';
import { COLLECTIONS_STORE_NAME, KitchenCollection, selectAvailableCollections } from 'modules/collections';
import { State } from 'store/types';
import { useIntersection } from 'common/hooks';
import { analyticsUtils } from 'utils/analytics-utils';
import { Link, PriceMediaCard, PriceMediaCardContent, Section, Typography } from 'common/components';
import { createElementKey, formatToRub } from 'utils';
import {
  BATHROOM_COLLECTIONS_CDN_PATH,
  BATHROOM_COLLECTIONS_ELEMENT_BLOCK,
} from 'landings/bathroom-collections/bathroom-collections-constants';
import { ProductSchema } from 'common/components/product-schema';
import {
  bathroomCollectionsListButton,
  bathroomCollectionsListButtonDisabled,
  bathroomCollectionsListGrid,
  bathroomCollectionsListImage,
  bathroomCollectionsListLink,
  bathroomCollectionsListLinkDisabled,
  bathroomCollectionsListName,
  bathroomCollectionsListPrice,
} from './bathroom-collections-list-styles';

export const BathroomCollectionsList: React.FC = () => {
  const productRef = useRef<HTMLAnchorElement[]>([]);
  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);

  const collectionList = selectAvailableCollections(collections) as KitchenCollection[];

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const numberPosition = Number(position);
    const collection = collectionList[numberPosition];

    analyticsUtils.sendEvent('GA:impressions', {
      elementType: 'product',
      elementBlock: BATHROOM_COLLECTIONS_ELEMENT_BLOCK,
      division: 'Сантехника',
      subdivision: 'Наборы мебели для ванной комнаты',
      komplektyId: collection.productId,
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price,
      productPosition: numberPosition + 1,
      abTestPrice: null,
      productPrevPrice: null,
      productTag: undefined,
    });
  });

  const handleCardClick = (collection: KitchenCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock: BATHROOM_COLLECTIONS_ELEMENT_BLOCK,
      elementName: 'productNameLink',
      division: 'Сантехника',
      subdivision: 'Наборы мебели для ванной комнаты',
      komplektyId: collection.productId,
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price,
      productPosition: position,
      abTestPrice: null,
      productPrevPrice: null,
      productTag: undefined,
    });
  };

  const handleCardnWheelClick = (e: MouseEvent, collection: KitchenCollection, position: number) => {
    if (e.button === 1) {
      handleCardClick(collection, position);
    }
  };

  return (
    <Section>
      <ul className={bathroomCollectionsListGrid}>
        {collectionList.map((collection, index) => (
          <li key={createElementKey('collection', index)}>
            <Link
              refCb={(el: HTMLAnchorElement | null) => {
                const position = el?.getAttribute('data-position');
                if (position && el) {
                  productRef.current[+position] = el;
                }
              }}
              data-position={index}
              className={cn(bathroomCollectionsListLink, {
                [bathroomCollectionsListLinkDisabled]: !collection.available,
              })}
              onAuxClick={e => handleCardnWheelClick(e, collection, index + 1)}
              onClick={() => handleCardClick(collection, index + 1)}
              href={collection.productUrl}
              title={collection.displayedName}>
              <PriceMediaCard>
                <div>
                  <img
                    className={bathroomCollectionsListImage}
                    alt={`Фото набора мебели для ванной ${collection.displayedName}`}
                    src={`${BATHROOM_COLLECTIONS_CDN_PATH}/collections-list${collection.mediaMainPhoto?.[0]}`}
                    width="377"
                    height="236"
                  />
                </div>
                <PriceMediaCardContent>
                  <Typography className={bathroomCollectionsListName} as="h2" variant="s" size="body_accent">
                    {collection.displayedName}
                  </Typography>
                  {collection.available ? (
                    <>
                      <Typography variant="caption_accent">
                        <span className={bathroomCollectionsListPrice}>
                          {formatToRub(collection.price)}{' '}
                        </span>
                        ₽
                      </Typography>
                      <Typography variant="s" size="body_accent" className={bathroomCollectionsListButton}>
                        Подробнее
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="s"
                      size="body_accent"
                      className={bathroomCollectionsListButtonDisabled}>
                      Нет в&nbsp;наличии
                    </Typography>
                  )}
                </PriceMediaCardContent>
              </PriceMediaCard>
            </Link>
          </li>
        ))}
      </ul>

      <ProductSchema />
    </Section>
  );
};
